import { Box, Text, LinkBox, LinkOverlay } from '@chakra-ui/layout'
import { Tag, TagLabel, TagLeftIcon } from '@chakra-ui/tag'
import React, { createElement, useState, useMemo, useRef, useEffect, useCallback } from 'react'
// import styled from '@emotion/styled'

// import BackgroundImage from '../../BackgroundImage'
import { getImageTextColor } from '../../hooks/useImageTextColor'
import RemoteIcon from '../RemoteIcon'
import RatioImage from '../RatioImage'
import useImpression from '../../hooks/useImpression'
import useTracker from '../../hooks/useTracker'
import makeLink from '../../helpers/makeLink'
import Link from '../../components/Link'
import { useMount, useMountedState, useSessionStorage } from 'react-use'
// import useNiceTextColor from '../../hooks/useNiceTextColor'

const gradientColor = {
  white: 'rgba(0,0,0,0.8)',
  black: 'rgba(255,255,255,0.8)',
}

// const StyledBox = styled(LinkBox)`
// &:hover {
//   .bg-image {
//     transform: scale(1.1);
//   }
// }
// `

const TagSticker = ({ bg, icon, label, align, inFrame }) => {
  // const [bgHex] = useToken('colors', [bg])
  // const color = useNiceTextColor(bgHex)
  const pos = inFrame ? 6 : 4
  return (
    <Box position="absolute" top={pos} {...{ [align || 'left']: pos }}>
      <Tag
        bg={bg || 'primary.500'}
        color="white"
        rounded="full"
        size="lg"
        border="1px solid currentColor"
      >
        {icon && (
          <TagLeftIcon as={p => <RemoteIcon src={icon} {...p} />} />
        )}
        <TagLabel>{label}</TagLabel>
      </Tag>
    </Box>
  )
}

const ImageText = ({ padding, variant, color: textColor, children }) => {
  const color = useMemo(() => {
    if (variant === 'frame') return 'custom.text'
    if (variant === 'solid') return 'white'
    if (variant === 'gradient') {
      return textColor
    }
    return textColor === 'white' ? 'black' : 'white'
  }, [textColor, variant])
  const hasGradient = variant === 'gradient' && gradientColor[textColor]

  const bg = useMemo(() => {
    if (variant === 'solid') return 'primary.500'
    if (variant === 'frame') return 'white'
  }, [variant])
  return (
    <Box
      position="absolute"
      left={(hasGradient && padding) || "0"}
      bottom={(hasGradient && padding) || "0"}
      right={(hasGradient && padding) || "0"}
      backgroundImage={hasGradient && `linear-gradient(transparent, ${gradientColor[textColor]} 90%)`}
      bg={bg}
      px={padding && variant !== 'frame' ? +padding + 4 : 4}
      pb="2"
      pt="2"
      minH="2.75em"
    >
      <Text
        fontSize="1.125em"
        fontWeight="500"
        color={color}
      >{children}</Text>
    </Box>
  )
}

const ImageBox = ({
  src,
  alt,
  data,
  tag,
  ratio = 1,
  textAlign,
  isSmall,
  isEditing,
  isPreview,
  frame,
  variant: vnt,
  id,
  linkId,
  blockId,
  profileId,
  ...props
}) => {
  const variant = vnt || 'gradient'

  const ref = useImpression(profileId, blockId, id, linkId)
  const clickTrack = useTracker('click')

  const [textColor, setTextColor] = useSessionStorage(`${id}-text-color`, 'white')
  const imageRef = useRef()

  const handleTextColor = useCallback(() => {
    if (variant === 'gradient' && imageRef.current) {
      const image = imageRef.current.querySelector('picture img')
      try {
        setTextColor(getImageTextColor(image))
      } catch (e) {
        console.error(e)
      }
    }
  }, [variant])

  useMount(() => {
    handleTextColor()
  });

  const isExternal = isPreview || data?.mode !== 'internal'
  const padding = (frame || variant === 'frame') && '4'
  return (
    <LinkBox
      width="100%"
      bg="white"
      rounded="md"
      overflow="hidden"
      transform="translateZ(0)"
      textAlign={textAlign ?? (isSmall && 'center')}
      padding={padding}
      boxShadow="md"
      mb="1"
      ref={!isEditing && !isPreview ? ref : undefined}
      {...props}
    >
      <RatioImage
        src={src}
        ratio={ratio}
        alt={alt || data?.text}
        ref={imageRef}
        onLoad={handleTextColor}
        size={isSmall ? 'small' : 'medium'}
        isEditing={isEditing}
        isPreview={isPreview}
        rounded="md"
        overflow="hidden"
      />
      {(data?.text || variant === 'solid') && (
        <ImageText
          color={textColor}
          variant={variant}
          padding={padding}
        >{data.text}</ImageText>
      )}
      {tag?.label && <TagSticker {...tag} inFrame={!!padding} />}
      <LinkOverlay
        as={data.url ? Link : 'p'}
        isExternal={isExternal}
        lineHeight="0"
        fontSize="0"
        height="0"
        display="block"
        onClick={() => !isEditing && !isPreview && clickTrack(profileId, blockId, id, linkId)}
        {...({ [isExternal ? 'href' : 'to']: data?.mode === 'internal' ? data.url : makeLink(data.url) })}
      >
        {data.text}
      </LinkOverlay>
    </LinkBox>
  )
}

export default ImageBox
